import { instance } from "./apiBase";

// CRM-1830: the company's pre-recorded voicemails. Every teammate who makes
// calls lists them to leave one on a call; admins manage them on Phone numbers.

export interface PrerecordedVoicemail {
  id: number;
  name: string;
  duration_seconds: number;
  added_by: { id: number; name: string | null } | null;
  created_at: string;
  updated_at: string;
}

interface VoicemailAudio {
  url: string;
  duration_seconds: number;
}

// The voicemail a teammate just left on their call.
interface LeftVoicemail {
  id: number;
  name: string;
  duration_seconds: number;
  status: "starting" | "playing" | "finished" | "not_finished";
}

// Shared by the softphone's picker and the Phone numbers tab, so an admin's
// change shows on the next call without a reload.
export const PRERECORDED_VOICEMAILS_QUERY_KEY = [
  "PRERECORDED_VOICEMAILS"
] as const;

const BASE = "/portal/v1/boom_crm/prerecorded_voicemails";

// `audio` is the WAV the portal made from the recording or upload; Twilio can
// only play MP3 or WAV.
const formOf = (input: { name?: string; audio?: Blob }) => {
  const form = new FormData();
  if (input.name !== undefined) form.append("name", input.name);
  if (input.audio) form.append("file", input.audio, "voicemail.wav");
  return form;
};

export const prerecordedVoicemailsApi = {
  async list(): Promise<PrerecordedVoicemail[]> {
    const { data } = await instance.get<{
      voicemails: PrerecordedVoicemail[];
    }>(BASE);
    return data.voicemails;
  },

  async create(input: {
    name: string;
    audio: Blob;
  }): Promise<PrerecordedVoicemail> {
    const { data } = await instance.post<PrerecordedVoicemail>(
      BASE,
      formOf(input)
    );
    return data;
  },

  // Rename sends only the name, Replace recording only the audio.
  async update(
    id: number,
    input: { name?: string; audio?: Blob }
  ): Promise<PrerecordedVoicemail> {
    const { data } = await instance.patch<PrerecordedVoicemail>(
      `${BASE}/${id}`,
      formOf(input)
    );
    return data;
  },

  async remove(id: number): Promise<void> {
    await instance.delete(`${BASE}/${id}`);
  },

  async audio(id: number): Promise<VoicemailAudio> {
    const { data } = await instance.get<VoicemailAudio>(`${BASE}/${id}/audio`);
    return data;
  },

  // Leaves a message on the teammate's live outgoing call: their own leg ends
  // at once and Boom plays it to the voicemail box.
  async leave(callId: number, voicemailId: number): Promise<LeftVoicemail> {
    const { data } = await instance.post<LeftVoicemail>(
      `/portal/v1/boom_crm/calls/${callId}/voicemail_drop`,
      { voicemail_id: voicemailId }
    );
    return data;
  },

  // The message left on a past call, for its Play buttons.
  async leftOnCall(callId: number): Promise<VoicemailAudio> {
    const { data } = await instance.get<VoicemailAudio>(
      `/portal/v1/boom_crm/calls/${callId}/voicemail_drop/audio`
    );
    return data;
  }
};
